import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { BookGrid } from '@/components/catalog/BookCard'
import { SearchBar } from '@/components/catalog/SearchBar'
import { Badge, Card, LoadingBlock, SectionHeading } from '@/components/ui'
import { useCatalogStats, useCategoryTree, useRecentBooks } from '@/hooks/useCatalog'
import { useLanguage } from '@/hooks/useDirection'
import { useLibraryInfo } from '@/hooks/useLibraryInfo'
import { formatAppointment, formatNumber } from '@/lib/format'
import { useState } from 'react'

export default function HomePage() {
  const { t } = useTranslation()
  const language = useLanguage()
  const navigate = useNavigate()
  const [term, setTerm] = useState('')

  const { data: info } = useLibraryInfo()
  const { data: stats } = useCatalogStats()
  const { data: recent, isLoading: loadingRecent } = useRecentBooks()
  const { data: tree } = useCategoryTree()

  const roots = tree?.results ?? []

  return (
    <div className="flex flex-col gap-10">
      {/* Hero */}
      <section className="rounded-[var(--radius-card)] bg-gradient-to-br from-brand to-brand-strong px-5 py-10 text-brand-contrast sm:px-10 sm:py-14">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-bold sm:text-4xl">
            {info?.library_name || t('home.heroTitle')}
          </h1>
          <p className="mt-3 text-sm text-white/85 sm:text-base">{t('home.heroSubtitle')}</p>

          {info && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Badge
                tone={info.is_open_now ? 'success' : 'neutral'}
                className="bg-white/15 text-white border-white/25"
              >
                {info.is_open_now ? t('home.openNow') : t('home.closedNow')}
              </Badge>
              {info.next_pickup_slot && (
                <span className="text-xs text-white/80">
                  {t('home.nextPickup')}:{' '}
                  {formatAppointment(info.next_pickup_slot, info.timezone_name, language)}
                </span>
              )}
            </div>
          )}

          <div className="mt-6 max-w-xl">
            <SearchBar
              value={term}
              onChange={setTerm}
              onSubmit={(value) =>
                navigate(value ? `/browse?q=${encodeURIComponent(value)}` : '/browse')
              }
            />
          </div>

          <div className="mt-5 flex flex-wrap gap-2.5">
            <Link
              to="/browse"
              className="rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-brand-strong transition hover:bg-white/90"
            >
              {t('home.browseCollection')}
            </Link>
            <Link
              to="/visit"
              className="rounded-lg border border-white/40 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10"
            >
              {t('home.visitUs')}
            </Link>
          </div>
        </div>
      </section>

      {/* Counters */}
      {stats && (
        <section className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
          {[
            { label: t('home.stats.books'), value: stats.books_total },
            { label: t('home.stats.available'), value: stats.books_available },
            { label: t('home.stats.categories'), value: stats.categories_total },
            { label: t('home.stats.authors'), value: stats.authors_total },
          ].map((item) => (
            <Card key={item.label} className="px-4 py-4 text-center">
              <p className="text-2xl font-bold text-brand-strong force-ltr">
                {formatNumber(item.value, language)}
              </p>
              <p className="mt-0.5 text-xs text-ink-muted">{item.label}</p>
            </Card>
          ))}
        </section>
      )}

      {/* Top-level categories */}
      {roots.length > 0 && (
        <section>
          <SectionHeading title={t('home.exploreCategories')} />
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {roots.map((node) => (
              <Card as="li" key={node.id} className="transition hover:shadow-[var(--shadow-raised)]">
                <Link
                  to={`/browse?category=${encodeURIComponent(node.slug)}`}
                  className="flex h-full flex-col justify-between gap-2 p-4"
                >
                  <span className="bidi-isolate text-sm font-semibold text-ink">{node.name}</span>
                  <span className="text-xs text-ink-subtle">
                    {t('catalog.booksInBranch', { count: node.total_book_count })}
                  </span>
                </Link>
              </Card>
            ))}
          </ul>
        </section>
      )}

      {/* Recently added */}
      <section>
        <SectionHeading
          title={t('home.recentlyAdded')}
          action={
            <Link to="/browse?ordering=-created_at" className="text-sm text-brand-strong hover:underline">
              {t('home.browseCollection')}
            </Link>
          }
        />
        {loadingRecent ? (
          <LoadingBlock />
        ) : (
          <BookGrid books={recent ?? []} />
        )}
      </section>
    </div>
  )
}
